import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper
} from '@mui/material';
import './lease-form.scss';
import { format, isValid, parse } from 'date-fns';

const getOrdinal = (day) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
};

const formatDateWithOrdinal = (dateString) => {
  if (!dateString) return 'Automatically filled when signed';
  const cleanDateStr = dateString.split('T')[0];
  const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
  if (!isValid(date)) return 'Automatically filled when signed';
  const month = format(date, 'MMMM');
  const dayWithOrdinal = getOrdinal(date.getDate());
  const year = format(date, 'yyyy');
  return `${month} ${dayWithOrdinal}, ${year}`;
};

const TenantDetails = ({ lease }) => (
  <Box className="section">
    <Typography variant="subtitle2" className="section-title">TENANTS AGREEMENT:</Typography>
    <Typography variant="body2" className="section-content" sx={{ mb: 1 }}>
      The tenant agrees that in addition to the occupants, the premises may be occupied only by the following persons:
    </Typography>

    <Paper variant="outlined" className="tenant-details-table" sx={{ mb: 2, borderRadius: 1 }}>
      <Table size="small">
        <TableBody>
          {lease.tenants?.length > 0 ? (
            lease.tenants.map((tenant, index) => (
              <TableRow key={index}>
                <TableCell className="label">Name</TableCell>
                <TableCell className="value">
                  {tenant.name || '-'}
                </TableCell>
                <TableCell className="label">Signature</TableCell>
                <TableCell className="value">
                  {tenant.signature ? (
                    <Box sx={{ height: 20 }}>
                      <img
                        src={tenant.signature}
                        alt="Signature"
                        style={{ maxWidth: '100%', maxHeight: '20px' }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 20,
                        border: '1px solid #eee',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#aaa',
                        fontStyle: 'italic'
                      }}
                    >
                      Awaiting signature
                    </Box>
                  )}
                </TableCell>
                <TableCell className="label">Date</TableCell>
                <TableCell className="value">{formatDateWithOrdinal(tenant.date) || 'Automatically filled when signed'}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ color: '#aaa' }}>
                No tenants available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  </Box>
);


const LandlordDetails = ({ lease }) => {
  const manager = lease?.manager || {
    first_name: "",
    last_name: "",
    phone_no: "",
    email: "",
    signature: "",
    date: "",
  };
  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">
        LANDLORD/LANDLORD AGENT:
      </Typography>

      <Paper variant="outlined" className="landlord-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label">Name</TableCell>
              <TableCell className="value">
                {manager.first_name + manager.last_name || '-'}
              </TableCell>
              <TableCell className="label">Signature</TableCell>
              <TableCell className="value">
                {manager.signature ? (
                  <Box sx={{ height: 20 }}>
                    <img
                      src={manager.signature}
                      alt="Signature"
                      style={{ maxWidth: '100%', maxHeight: '20px' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 20,
                      border: '1px solid #eee',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#aaa',
                      fontStyle: 'italic',
                    }}
                  >
                    Awaiting signature
                  </Box>
                )}
              </TableCell>
              <TableCell className="label">Date</TableCell>
              <TableCell className="value">{formatDateWithOrdinal(manager.date) || 'Automatically filled when signed'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const BuildingAddressDetails = ({ lease }) => {
  const formatAddress = () => {
    const { property_details } = lease;

    if (!property_details) return '';

    const { building_name, address, postal_code, province, city } = property_details;
    const mainAddress = address || building_name;
    const addressParts = [mainAddress, city, province, postal_code];

    return addressParts.filter(Boolean).join(', ');
  };

  const phone = lease.manager?.phone_no || 'N/A';
  const email = lease.manager?.email || 'N/A';

  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">BUILDING ADDRESS AND CONTACT:</Typography>

      <Paper variant="outlined" className="building-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label" sx={{ width: '15%' }}>
                Address:
              </TableCell>
              <TableCell className='value' sx={{ width: '45%' }}>
                <Box sx={{ mb: 1 }}>
                  {formatAddress() || <span style={{ color: '#aaa' }}>No address available</span>}
                </Box>
              </TableCell>
              <TableCell className="label-2" sx={{ width: '15%' }}>Phone & Email:</TableCell>
              <TableCell className='value-2' sx={{ width: '25%' }}>
                <Box sx={{ mb: 0.5 }}>{phone}</Box>
                <Box>{email}</Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const LeaseTermSection = ({ title, content }) => (
  <Box className="section">
    <Typography variant="subtitle2" className="section-title">{title}</Typography>
    <Typography variant="body2" className="section-content">
      {content}
    </Typography>
  </Box>
);

const Step6 = ({ lease }) => {
  const leaseSections = [
    {
      title: "22. LANDLORD'S AGENT",
      content: "It is understood and agreed by and between the parties hereto that the landlord shall have the right to appoint such agents to act on his behalf, and deal with the tenant pursuant to this lease, as the landlord shall consider necessary and for the lease."
    },
    {
      title: "23. LOCKS, KEYS, PARKING PASS, FOBS",
      content: "The cost of replacing any lost suite keys, Fobs, Mailbox keys, Parking Pass will be borne by the tenant. No locks are to be installed or changed by the tenant. All keys to the premises and related facilities shall be delivered up to the landlord upon termination of this lease. If the landlord is required to change any locks due to keys not being delivered promptly to the landlord, the tenant shall immediately pay for all costs incurred. (Tenants are responsible for the cost of any lock outs- $250 one-time charge). If the Management supplies you with a replacement item, the charge to the tenant will be $150.00 per item. Full payment must be made in full prior to the landlord providing replacement item to the tenant."
    },
    {
      title: "24. HEAT AND HOT WATER",
      content: "In the event of an interruption of heat or hot water due to mechanical failure, boiler malfunction, breakdown, circumstances beyond the Landlord’s control, or required repairs, the Landlord shall take reasonable steps to restore service within a reasonable time after becoming aware of the issue. The Landlord shall not be liable for indirect or consequential damages, including alternate accommodation costs, except where such liability arises from the Landlord’s failure to meet obligations under the Residential Tenancies Act (Alberta)."
    },
    {
      title: "25. WAIVER TERMS & ACCEPTANCE",
      content: "The waiver of any terms of this contract at any time shall not be deemed a waiver on any occasion or of any other term. Any violation of these conditions shall constitute a breach of the tenancy, and the landlord shall thereafter be entitled to possession after giving a 14 day notice under section 29(1) of RTA."
    },
    {
      title: "26. ACCEPTANCE OF LEASE",
      content: "The Tenant does hereby accept the lease of the Premises to be held as tenant, subject to the conditions, restrictions and covenants contained within this Agreement or attached hereto."
    }
  ];

  return (
    <Box className="lease-form-step6">
      {leaseSections.map((section, index) => (
        <LeaseTermSection
          key={index}
          title={section.title}
          content={section.content}
        />
      ))}

      <Box className="signature">
        <span className="notice">
          IF THERE IS MORE THAN ONE TENANT, THEIR LIABILITY SHALL BE JOINT IN SEVERAL.
        </span>
        <br />
        <span className="notice">
          THE DATE OF THIS AGREEMENT SHALL BE THE DATE THAT THE LAST OF THE PARTIES HAVE SIGNED.
        </span>
        <br />
        <TenantDetails lease={lease} />
        <LandlordDetails lease={lease} />
        <BuildingAddressDetails lease={lease} />
      </Box>
    </Box>
  );
};

export { Step6 };